import { Checkbox, Divider, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { t } from 'i18next';
import { Fragment } from 'react';
import type { CostByInitiativeResponse } from '../../../../api/financials-client/financials-client.type';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  setCostByInitiativeInitiativeIds,
  setCostByInitiativeTrendLines,
} from '../../../../store/financials-store/financials-store.actions';
import { newCOLORS } from '../../../../styles/colors';
import { Icon } from '../../../../ui-library/icon/icon';
import { SmallSquareButton } from '../shared/small-square-button';
import { getInitiativeInfo } from './cost-by-initiative.helpers';

interface FiltersProps {
  setShow: (show: boolean) => void;
  data?: CostByInitiativeResponse[];
  isPending: boolean;
}

function Filters({ setShow, data, isPending }: FiltersProps) {
  const [isMeasuresOpen, { toggle: toggleMeasures }] = useDisclosure(true);
  const [isTrendsOpen, { toggle: toggleTrends }] = useDisclosure(true);

  const { selectedInitiativeIds, selectedTrendLines } = useFinancialsStore((state) => state.filters.costByInitiative);

  const initiativeInfo = getInitiativeInfo(data);

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Text fw={750}>Filters</Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      {!isPending && Object.keys(data || {}).length === 0 ? (
        <ContentPlaceholder icon="event_busy" text={t('strategy.initiative_performance.no_filtered_data')} />
      ) : (
        <Fragment>
          <Group justify="space-between">
            <Group>
              <Icon name="adjust" size={24} color={newCOLORS.gray} />
              <Text fw={750}>Initiatives</Text>
            </Group>
            <Icon
              name={isMeasuresOpen ? 'unfold_less' : 'unfold_more'}
              size={24}
              color={newCOLORS.gray}
              onClick={toggleMeasures}
              style={{ cursor: 'pointer' }}
            />
          </Group>
          <Divider />

          <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
            {isMeasuresOpen && Object.keys(initiativeInfo).length > 0 ? (
              <Stack>
                <Checkbox.Group
                  value={selectedInitiativeIds}
                  onChange={(value) => setCostByInitiativeInitiativeIds(value as string[])}
                >
                  {Object.entries(initiativeInfo).map(([id, name]) => (
                    <Checkbox
                      key={id}
                      value={id}
                      label={name}
                      mb={8}
                      styles={{
                        input: { cursor: 'pointer' },
                      }}
                    />
                  ))}
                </Checkbox.Group>
              </Stack>
            ) : (
              isMeasuresOpen && (
                <Stack align="center" p="md">
                  <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                    {t('financials.no_filtered_data')}
                  </Text>
                </Stack>
              )
            )}
            {selectedInitiativeIds.length > 0 && (
              <Stack>
                <Divider />
                <Group justify="space-between">
                  <Group>
                    <Icon name="filter_tilt_shift" size={24} color={newCOLORS.gray} />
                    <Text fw={750}>Trends</Text>
                  </Group>
                  <Icon
                    name={isTrendsOpen ? 'unfold_less' : 'unfold_more'}
                    size={24}
                    color={newCOLORS.gray}
                    onClick={toggleTrends}
                    style={{ cursor: 'pointer' }}
                  />
                </Group>
                <Divider />
                {isTrendsOpen && (
                  <Checkbox.Group
                    value={selectedTrendLines}
                    onChange={(value) => setCostByInitiativeTrendLines(value as string[])}
                  >
                    {Object.entries(initiativeInfo)
                      .filter(([id]) => selectedInitiativeIds.includes(id))
                      .map(([id, name]) => (
                        <Checkbox
                          key={id}
                          value={id}
                          label={name}
                          mb={8}
                          styles={{
                            input: { cursor: 'pointer' },
                          }}
                        />
                      ))}
                  </Checkbox.Group>
                )}
              </Stack>
            )}
          </ScrollArea>
        </Fragment>
      )}
    </Stack>
  );
}

export { Filters };
